import { useMemo } from "react";
import { DataTableColumn } from "../types";

export interface GroupingOptions<TRow> {
  columns: DataTableColumn<TRow>[];
  rows: TRow[];
}

export interface GroupingState<TRow> {
  root: GroupNode<TRow> | null;
  groupColumn: DataTableColumn<TRow> | null;
}

export interface GroupNodeConfig {
  key: string;
  label?: string;
  labelKey?: string;
  localKey?: string;
  localScope?: string[];
  icon?: string;
  iconKey?: string;
}

export interface GroupNode<TRow = any> extends GroupNodeConfig {
  rows: TRow[];
  nodes: GroupNode<TRow>[];
  depth: number;
  isExpanded?: boolean;
}

function wrap<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}

function makeRoot<TRow>(): GroupNode<TRow> {
  return { key: "", rows: [], nodes: [], depth: 0 };
}

function makeNode<TRow>(
  props: string | Partial<GroupNode<TRow>>,
  _column?: DataTableColumn<TRow>,
  depth: number = 0,
): GroupNode<TRow> {
  let nodeProps: Partial<GroupNode<TRow>>;

  if (typeof props === "string") {
    nodeProps = {
      key: props,
      labelKey: props,
    };
  } else {
    nodeProps = props;
  }

  return {
    key: nodeProps.key || "",
    label: nodeProps.label,
    labelKey: nodeProps.labelKey,
    localKey: nodeProps.localKey,
    localScope: nodeProps.localScope,
    icon: nodeProps.icon,
    iconKey: nodeProps.iconKey,
    rows: [],
    nodes: [],
    depth,
    isExpanded: nodeProps.isExpanded,
  };
}

function insertRow<TRow>(
  parentNode: GroupNode<TRow>,
  groupColumn: DataTableColumn<TRow>,
  row: TRow,
  path?: any[],
): GroupNode<TRow> {
  if (!path) {
    const value = (row as any)[groupColumn.id];
    return insertRow(
      parentNode,
      groupColumn,
      row,
      groupColumn.groupPath ? groupColumn.groupPath(value) : wrap(value),
    );
  }

  if (path.length === 0) {
    return { ...parentNode, rows: [...parentNode.rows, row] };
  }

  const node = makeNode<TRow>(path[0], groupColumn, parentNode.depth + 1);
  const index = parentNode.nodes.findIndex((n) => n.key === node.key);

  if (index > -1) {
    const updatedNodes = [...parentNode.nodes];
    updatedNodes[index] = insertRow(
      parentNode.nodes[index],
      groupColumn,
      row,
      path.slice(1),
    );
    return { ...parentNode, nodes: updatedNodes };
  }

  return {
    ...parentNode,
    nodes: [
      ...parentNode.nodes,
      insertRow(node, groupColumn, row, path.slice(1)),
    ],
  };
}

function getTotalRows<TRow>(node: GroupNode<TRow>): TRow[] {
  return node.nodes.reduce((acc, n) => [...acc, ...getTotalRows(n)], node.rows);
}

export function useGrouping<TRow>(
  options: GroupingOptions<TRow>,
): GroupingState<TRow> {
  const { columns, rows } = options;

  const groupColumn = useMemo(
    () => columns.find((col) => col.group) ?? null,
    [columns],
  );

  const root = useMemo<GroupNode<TRow> | null>(() => {
    if (!groupColumn) return null;

    let rootNode = makeRoot<TRow>();
    rows.forEach((row) => {
      rootNode = insertRow(rootNode, groupColumn, row);
    });

    return rootNode;
  }, [groupColumn, rows]);

  return {
    root,
    groupColumn,
  };
}

export { getTotalRows, makeNode, makeRoot, insertRow };
